import { useEffect, useState } from 'react';
import {
  History,
  Loader2,
  FileText,
  CheckCircle2,
  FilePlus2,
  AlertCircle,
  XCircle,
  Euro,
  CalendarClock,
  Bell,
  PenLine,
  PenTool,
  ScrollText,
  Receipt,
  Banknote,
  X,
  FileCheck2,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';

type CustomerEvent = {
  id: number;
  customerId: number;
  vorgangId: number | null;
  vorgangsnummer: string | null;
  source: string;
  typ: string;
  beschreibung: string;
  occurredAt: string;
};

type EventResponse = { items: CustomerEvent[]; total: number };

// Der Verlauf liegt unter einem eigenen Reiter und konkurriert nicht mehr mit den
// Dokumenten. Sichtbar bleiben die drei jüngsten Einträge; alles Ältere steht im Popup.
const VISIBLE_LIMIT = 3;

/** Icon + Akzentfarbe pro Event-Typ. Fallback ist FileText/Slate. */
const TYP_META: Record<string, { Icon: typeof FileText; color: string }> = {
  // Mail-Monitor
  antrag_gestellt:                  { Icon: Send,         color: 'text-blue-500' },
  antrag_gestartet:                 { Icon: Send,         color: 'text-slate-400' },
  antrag_hinzugefuegt:              { Icon: FilePlus2,    color: 'text-blue-500' },
  erlaubnis_erteilt:                { Icon: CheckCircle2, color: 'text-teal-500' },
  bescheid_hinzugefuegt:            { Icon: CheckCircle2, color: 'text-emerald-500' },
  verwendungsnachweis_hinzugefuegt: { Icon: ScrollText,   color: 'text-indigo-500' },
  dokument_hinzugefuegt:            { Icon: FileText,     color: 'text-slate-500' },
  nachforderung_eingegangen:        { Icon: AlertCircle,  color: 'text-amber-500' },
  ablehnung_eingegangen:            { Icon: XCircle,      color: 'text-rose-500' },
  auszahlung_bestaetigt:            { Icon: Euro,         color: 'text-emerald-600' },
  frist_verlaengert:                { Icon: CalendarClock,color: 'text-cyan-500' },
  mahnung_eingegangen:              { Icon: Bell,         color: 'text-orange-500' },
  // Report
  bericht_erstellt:                 { Icon: FileCheck2,   color: 'text-violet-500' },
  verwendungsnachweis_erstellt:     { Icon: FileCheck2,   color: 'text-indigo-500' },
  // Signing
  bericht_zur_unterschrift:         { Icon: PenLine,      color: 'text-fuchsia-500' },
  bericht_unterschrieben:           { Icon: PenTool,      color: 'text-fuchsia-600' },
  verwendungsnachweis_zur_unterschrift: { Icon: PenLine,  color: 'text-fuchsia-500' },
  verwendungsnachweis_versand_problem:  { Icon: AlertCircle, color: 'text-red-500' },
  verwendungsnachweis_unterschrieben:   { Icon: PenTool,  color: 'text-fuchsia-600' },
  verwendungsnachweis_angefordert:      { Icon: Send,     color: 'text-purple-500' },
  dokument_gegengezeichnet:             { Icon: PenTool,  color: 'text-fuchsia-500' },
  unterschriebener_bericht_aktualisiert: { Icon: PenTool, color: 'text-fuchsia-600' },
  // Manual Upload
  vollmacht_hochgeladen:            { Icon: FileText,     color: 'text-blue-400' },
  grc_hochgeladen:                  { Icon: FileText,     color: 'text-blue-400' },
  deminimis_hochgeladen:            { Icon: FileText,     color: 'text-blue-400' },
  rechnung_hochgeladen:             { Icon: Receipt,      color: 'text-amber-600' },
  kontoauszug_hochgeladen:          { Icon: Banknote,     color: 'text-emerald-500' },
  verwendungsnachweis_hochgeladen:  { Icon: ScrollText,   color: 'text-indigo-500' },
  // Signing-Portal (KI-erkannt)
  dokument_unterschrieben:          { Icon: PenTool,      color: 'text-fuchsia-600' },
  dokument_mismatch:                { Icon: AlertCircle,  color: 'text-amber-500' },
  // Manual Delete
  dokument_geloescht:               { Icon: Trash2,       color: 'text-rose-500' },
  dokument_wiederhergestellt:       { Icon: Undo2,        color: 'text-emerald-500' },
};

/** Signing-Typen, die trotz source==='signing' KEINE Kunden-Aktion sind (Berater-/System-Versand
 *  bzw. eine vom Kunden hochgeladene, aber nicht eindeutig zugeordnete Datei zur Prüfung). */
const KEINE_KUNDEN_TYPEN = new Set(['bericht_zur_unterschrift', 'verwendungsnachweis_zur_unterschrift', 'verwendungsnachweis_versand_problem', 'dokument_mismatch']);

/** Ein Event stammt „vom Kunden", wenn es über den Signing-Link ausgelöst wurde UND keine der
 *  obigen Berater-/System-Aktionen ist. Solche Einträge werden im Verlauf rot hervorgehoben. */
const istVomKunden = (ev: CustomerEvent) => ev.source === 'signing' && !KEINE_KUNDEN_TYPEN.has(ev.typ);

/** Höchstens zwei Zeilen, danach „…" — nur in der Kurzliste. */
const zweiZeilen: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

/** Eine Verlaufszeile. In der Kurzliste gekürzt und zurückhaltend gefärbt — sie ist
 *  dort Beiwerk neben den Dokumenten. Im Popup steht der volle Text im vollen Kontrast. */
function EventZeile({ e, darkMode, kompakt }: { e: CustomerEvent; darkMode: boolean; kompakt?: boolean }) {
  const meta = TYP_META[e.typ] ?? { Icon: FileText, color: 'text-slate-500' };
  const Icon = meta.Icon;
  const vomKunden = istVomKunden(e);
  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <li className={`flex items-start gap-2 border-l-2 pl-2 ${vomKunden ? 'border-red-500' : 'border-transparent'}`}>
      {/* In der Kurzliste bleiben die Symbole neutral — dort geht es um den Überblick,
          nicht um Typenkunde. Die Farben stehen im Popup. Der rote Strich links bleibt:
          er zeigt, dass der Kunde selbst gehandelt hat. */}
      <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
        kompakt ? 'text-slate-400 dark:text-slate-500' : vomKunden ? 'text-red-600' : meta.color
      }`} />
      <div className="min-w-0">
        <div
          style={kompakt ? zweiZeilen : undefined}
          title={kompakt ? e.beschreibung : undefined}
          className={`text-xs ${
            kompakt
              ? (darkMode ? 'text-slate-400' : 'text-slate-500')
              : (darkMode ? 'text-slate-100' : 'text-slate-800')
          }`}
        >
          {e.beschreibung}
          {e.vorgangsnummer && (
            <span
              className={`ml-1.5 inline-block text-[12px] font-medium px-1.5 py-0.5 rounded ${
                darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'
              }`}
              title="Vorgangsnummer"
            >
              {e.vorgangsnummer}
            </span>
          )}
        </div>
        <div className={`text-[13px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{fmt(e.occurredAt)}</div>
      </div>
    </li>
  );
}

export function EventTimelineTab({ customerId }: { customerId: number }) {
  const { darkMode } = useDarkMode();
  const [events, setEvents] = useState<CustomerEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [popupOffen, setPopupOffen] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get<EventResponse>(`/api/customer-events?customerId=${customerId}`)
      .then((res) => {
        if (!active) return;
        setEvents(res.items ?? []);
        setTotal(res.total ?? 0);
      })
      .catch(() => { if (active) { setEvents([]); setTotal(0); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [customerId]);

  const visible = events.slice(0, VISIBLE_LIMIT);
  const hiddenCount = Math.max(0, events.length - VISIBLE_LIMIT);

  return (
    <div>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Wird geladen…</div>
      ) : events.length === 0 ? (
        <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Noch keine Änderungen erfasst.</p>
      ) : (
        <>
          <ul className="space-y-2">
            {visible.map((e) => <EventZeile key={e.id} e={e} darkMode={darkMode} kompakt />)}
          </ul>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setPopupOffen(true)}
              className={`mt-3 flex items-center gap-1 text-[13px] font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
            >
              <History className="w-3 h-3" /> Alle {total} Einträge anzeigen
            </button>
          )}
        </>
      )}

      {/* Vollständiger Verlauf als Popup — die Spalte bleibt dadurch kurz. */}
      {popupOffen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPopupOffen(false)}>
          <div
            className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
              <History className="w-4 h-4 text-slate-500 dark:text-slate-400" />
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Änderungsverlauf</h3>
              <span className="text-xs text-slate-400">{total}</span>
              <button
                onClick={() => setPopupOffen(false)}
                aria-label="Schließen"
                className="ml-auto p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="space-y-2 overflow-y-auto px-5 py-4">
              {events.map((e) => <EventZeile key={e.id} e={e} darkMode={darkMode} />)}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
